import VaccinesDetailView from 'routes/apps/vaccines/CandidateRanking'

import { ExtractModelDataUnion } from 'interfaces/model.interface'
import { ValueType } from 'interfaces/valueType.interface'

import { SortOrders } from 'enums/SortOrders'

import {
  PressReleasesModel,
  MediaModel,
  ClinicalTrialsModel,
  PresentationsModel,
  CandidateRankingModel,
  CountriesModel,
  DealsModel,
  OrganisationsModel,
} from 'config/common/baseModel'
import { ApprovalsModel } from 'config/common/baseModel/Approvals'
import { customFilterNaNGraphData } from 'config/common/transformValue/charts'

import {
  compose,
  addOrOverrideColumn,
  whiteListColumn,
  renameColumn,
  updateColumnAttributes,
  blackListColumn,
} from 'utils/overrideModel'

const PhaseSortingObject = {
  sortOrderObject: [
    'null',
    'Validation',
    'Not Applicable',
    'Unknown',
    'Inactive',
    'Discontinued',
    'Paused',
    'Product Launch',
    'Approved',
    'Phase IV',
    'Preclinical',
    'Phase 0',
    'Phase I',
    'Phase I/II',
    'Phase II',
    'Phase II/III',
    'Phase III',
    'Phase III/IV',
  ],
}

const CandidatesBaseColumns: Array<
  ExtractModelDataUnion<typeof CandidateRankingModel>
> = [
  'candidateName',
  'latestPhaseManual',
  'sponsors',
  'alternativeNames',
  'type',
  'activeDevelopment',
  'targetDomain',
  'mechanismOfActionDescription',
  'vaccineAdjuvants',
  'routeOfAdministration',
  'afEmergencyApproval',
  'afFullApproval',
  'targetViralStrain',
  'afPanCoronavirusVaccine',
]

const candidatesModifier = [
  renameColumn('vaccine', 'Name'),
  renameColumn('stage', 'Phase'),
  renameColumn('antigenDiversity', 'Target Domains'),
  renameColumn('deliveryMethod', 'Routes of Administration'),
  renameColumn('targetVariant', 'Target Strains'),
  blackListColumn([
    'afNumberOfDoses',
    'transportShipping',
    'storage',
    'afShelfLife',
    'afDoseQuantity',
  ]),
  updateColumnAttributes('latestPhaseManual', PhaseSortingObject),
]

export const VaccinesCandidatesModel = compose<typeof CandidateRankingModel>(
  whiteListColumn([
    ...CandidatesBaseColumns,
    'afDoseQuantity',
    'afPanSarsCov2Vaccine',
    'combinationVaccine',
    'latestPhaseManualSource',
  ]),
  ...candidatesModifier
)({
  ...CandidateRankingModel,
  name: 'Candidates',
  defaultSortObject: [
    {
      id: 'latestPhaseManual',
      sortOrder: SortOrders.DESC,
    },
    {
      id: 'candidateName',
      sortOrder: SortOrders.ASC,
    },
  ],
  renderDetailView: VaccinesDetailView,
})

export const VaccinesPreclinicalCandidates = compose<
  typeof CandidateRankingModel
>(
  whiteListColumn([
    ...CandidatesBaseColumns,
    'afPanSarsCov2Vaccine',
    'combinationVaccine',
    'latestPhaseManualSource',
  ]),
  ...candidatesModifier
)({
  ...CandidateRankingModel,
  name: 'Preclinical',
  defaultSortKey: 'stage',
  defaultSortOrder: SortOrders.ASC,
  renderDetailView: VaccinesDetailView,
})

export const VaccinesPhaseIOrIICandidates = compose<
  typeof CandidateRankingModel
>(
  whiteListColumn([
    ...CandidatesBaseColumns,
    'afPanSarsCov2Vaccine',
    'combinationVaccine',
    'latestPhaseManualSource',
  ]),
  ...candidatesModifier
)({
  ...CandidateRankingModel,
  name: 'Phase I or II',
  defaultSortKey: 'stage',
  defaultSortOrder: SortOrders.ASC,
  renderDetailView: VaccinesDetailView,
})

export const VaccinesPhaseIIIOrIVCandidates = compose<
  typeof CandidateRankingModel
>(
  whiteListColumn([
    ...CandidatesBaseColumns,
    'afDoseQuantity',
    'afPanSarsCov2Vaccine',
    'combinationVaccine',
    'latestPhaseManualSource',
  ]),
  ...candidatesModifier
)({
  ...CandidateRankingModel,
  name: 'Phase III or IV',
  defaultSortKey: 'stage',
  defaultSortOrder: SortOrders.ASC,
  renderDetailView: VaccinesDetailView,
})

export const CombinationVaccineCandidates = compose<
  typeof CandidateRankingModel
>(
  whiteListColumn([
    'candidateName',
    'alternativeNames',
    'type',
    'combinationVaccine',
    'latestPhaseManual',
    'activeDevelopment',
    'sponsors',
    'affiliates',
    'routeOfAdministration',
    'targetDomain',
    'mechanismOfActionDescription',
    'latestPhaseManualSource',
  ])
)({
  ...CandidateRankingModel,
  name: 'Combination Vaccine',
  defaultSortKey: 'latestPhaseManual',
  defaultSortOrder: SortOrders.ASC,
  renderDetailView: VaccinesDetailView,
})

export const DemoCountriesModel = compose<typeof CountriesModel>(
  whiteListColumn([
    'name',
    'afSupplyDealVaccine',
    'luEmergencyApproval',
    'luFullApproval',
    'afTotalVaccinations',
    'afTotalDeliveries',
    'afVaccinesDelivered',
    'afPeopleFullyVaccinated',
    'afBoostersAdministered',
  ])
)({ ...CountriesModel, defaultSortKey: 'uiCandidateCount' })

export const VaccinesCandidateRankingModel = whiteListColumn<
  typeof CandidateRankingModel
>([
  'vaccine',
  'type',
  'stage',
  'afNumberOfDoses',
  'transportShipping',
  'storage',
  'antigenDiversity',
  'afSumSupplyDealsAgreed',
  'afSumProduction',
  'countryFirstDetected',
  'actionMechanism',
  'deliveryMethod',
  'leads',
  'views',
  'downloads',
  'tweets',
  'preprintMentions',
  'deals',
  'numberOfArticles',
  'afFilingForApproval',
  'afFullApproval',
  'targetVariant',
  'afPanCoronavirusVaccine',
  'afShelfLife',
])({
  ...CandidateRankingModel,
  name: 'Vaccines',
})

export const VaccinesNeutralisingAntibodiesModel = whiteListColumn<
  typeof CandidateRankingModel
>([
  'linkAfTrUniqueTreatmentNameToBnf',
  'priority',
  'lookupAfTrClinicalTrialStageFromBnf',
  'lookupAfTrMechanismOfActionFromBnf',
  'lookupAfTrRouteOfAdministrationFromBnf',
  'afAntibodyType',
  'researchInstitutions',
  'companies',
  'downloads',
  'views',
  'preprintMentions',
  'numberOfArticles',
])({
  ...CandidateRankingModel,
})

export const VaccinesApprovalGuidelinesModel = whiteListColumn<
  typeof PresentationsModel
>([
  'countries',
  'title',
  'publicationDate',
  'topics',
  'language',
  'articleLink',
])({
  ...PresentationsModel,
  name: 'Vaccine Approval Guidelines',
})

export const VaccinesStrategyPapersModel = compose<typeof PresentationsModel>(
  whiteListColumn([
    'title',
    'publicationDate',
    'journals',
    'channel',
    'otherKeywords',
  ]),
  addOrOverrideColumn({
    key: 'title',
    width: 750,
  })
)({
  ...PresentationsModel,
  name: 'Strategy Papers',
  defaultSortOrder: SortOrders.DESC,
})

export const VaccinesNeutralisingAntibodiesScienceModel = whiteListColumn<
  typeof PresentationsModel
>([
  'title',
  'publicationDate',
  'journals',
  'channel',
  'topics',
  'authors',
  'articleType',
  'sentiment',
  'reviewStatus',
  'articleLink',
  'candidates',
  'abstract',
  'articleViews',
  'pdfDownloads',
  'journalImpactScore',
])({
  ...PresentationsModel,
  name: 'Neutralising Antibodies',
})

export const VaccinesBoosterTrialsModel = whiteListColumn<
  typeof ClinicalTrialsModel
>([
  'vaccine',
  'title',
  'acronym',
  'studyTypeClean',
  'enrolledAt',
  'completedAt',
  'sponsors',
  'url',
  'status',
  'ages',
  'locations',
  'outcomeMeasures',
  'primaryOutcome',
  'secondaryOutcome',
  'participants',
])({
  ...ClinicalTrialsModel,
  name: 'Trials',
  displayKey: 'vaccine',
})

export const VaccinesTrialsModel = compose<typeof ClinicalTrialsModel>(
  whiteListColumn([
    'id',
    'title',
    'acronym',
    'soleCandidates',
    'comparatorCandidates',
    'sponsors',
    'latestPhaseFinal',
    'status',
    'startDate',
    'enroledAt',
    'primaryCompletionDate',
    'completedAt',
    'targetEnrolment',
    'numberOfParticipants',
    'studyType',
    'ages',
    'rawPrimaryOutcomeMeasures',
    'areas',
    'url',
  ])
)({
  ...ClinicalTrialsModel,
  displayKey: 'id',
  searchField: 'soleCandidates',
  defaultSortKey: 'completedAt',
  defaultSortOrder: SortOrders.DESC,
})

export const VaccinesClinicalTrialsConfirmedMedia = whiteListColumn<
  typeof DealsModel
>([
  'lookupVaccineName',
  'afDealType',
  'countries',
  'afPhase3TrialSize',
  'mediaUrls',
  'pressUrls',
])({
  ...DealsModel,
})

export const VaccinesOrganisationsModel = whiteListColumn<
  typeof OrganisationsModel
>(['name', 'companyUrl', 'newsroomUrl'])(OrganisationsModel)

export const VaccinesCountriesModel = compose<typeof CountriesModel>(
  whiteListColumn([
    'name',
    'luInfectionRatePer10000',
    'afSupplyDealVaccine',
    'afWillingnessToTakeA_COVID19Vaccine',
    'luEmergencyApproval',
    'luFullApproval',
    'afTotalVaccinations',
    'afTotalDeliveries',
    'afVaccinesDelivered',
    'luCovaxH1Deliveries',
    'afPeopleVaccinatedOneDose',
    'afPeopleFullyVaccinated',
    'luSuspendedUseVaccineName',
    'fAged20Plus',
    'afTotalCasesB16172',
    'afPrevalenceB117',
    'afPrevalenceP1',
    'afPrevalenceB16172',
    'afPrevalenceB1351',
    'afPolicyForPreviouslyInfectedNotes',
    'afPolicyForPreviouslyInfectedLink',
    'afThirdDosePolicy',
    'afPolicyForThirdDosesUrl',
    'afBoostersAdministered',
    'visaguideVaccineInvalid',
    'visaguideVaccineValid',
    'afBoostersAdministeredPerHundred',
    'timeInterval',
    'boosterVaccineCandidate',
    'afStartDateForThirdDoses',
  ]),
  addOrOverrideColumn(
    {
      key: 'uiTrialToCandidateRatio',
      width: 240,
      type: ValueType.TEXT,
      label: '% of candidates in clinical trials',
      accessor: (d: any) =>
        d.uiTrialToCandidateRatio ? `${d.uiTrialToCandidateRatio}%` : null,
    },
    3
  )
)({ ...CountriesModel, defaultSortKey: 'uiCandidateCount' })

export const VaccinesSupplyModel = compose<typeof CountriesModel>(
  whiteListColumn([
    'name',
    'population',
    'afEconomyIncomeBand',
    'afContinent',
    'afSupplyDealVaccine',
    'sumAgreedSupply',
    'afSumPotentialExpansion',
    'afSumSupplyAndPotentialExpansion',
    'afTotalDeliveries',
    'fDeliveriesToDateExcThoseDonated',
    'fBilateralDeliveryToDate',
    'fCovaxDeliveryToDate',
    'fDirectDonationDeliveryToDate',
  ]),
  renameColumn('afEconomyIncomeBand', 'Income Band of Recipient Country'),
  renameColumn('afContinent', 'Continent of the Recipient Country'),
  renameColumn('afTotalDeliveries', 'Total Deliveries'),
  renameColumn(
    'fDirectDonationDeliveryToDate',
    'Doses Delivered from Bilateral Donations'
  ),
  renameColumn(
    'fCovaxDeliveryToDate',
    'Doses Delivered from COVAX (inc. donations and COVAX direct)'
  ),
  renameColumn(
    'fBilateralDeliveryToDate',
    'Doses delivered from Bilateral or Regional deals'
  ),
  renameColumn(
    'fDeliveriesToDateExcThoseDonated',
    'Doses Delivered exc. Doses Donated Out'
  ),
  renameColumn('afSupplyDealVaccine', 'Vaccines Secured'),
  renameColumn(
    'sumAgreedSupply',
    'Number of Vaccine Doses Secured via Bilateral and Regional Deals'
  ),
  renameColumn('afSumPotentialExpansion', 'Potential Expansion'),
  renameColumn(
    'afSumSupplyAndPotentialExpansion',
    'Number of Vaccine Doses Secured via Bilateral and Regional Deals (Inc. Potential Expansion)'
  )
)({ ...CountriesModel, name: 'Country Supply' })

export const VaccinesDealsModel = compose<typeof DealsModel>(
  whiteListColumn([
    'lookupVaccineName',
    'afDealType',
    'afPricePerVaccineTreatment',
    'countries',
    'companyRegions',
    'mediaUrls',
    'pressUrls',
  ])
)({ ...DealsModel, name: 'Price' })

export const VaccinesProductionDealsModel = compose<typeof DealsModel>(
  whiteListColumn([
    'lookupVaccineName',
    'afDealType',
    'organisations',
    'dealCancelled',
    'countries',
    'afExpectedProduction',
    'afDateExpectedProductionLastAnnounced',
    'afPublicExpectedProductionNotes',
    'afTypeOfProduction',
    'afDealContracted',
    'afDateDealMade',
    'mediaUrls',
    'pressUrls',
  ]),
  renameColumn('countries', 'Country source of production')
)({ ...DealsModel, name: 'Manufacturing and Production Table' })

export const VaccinesApprovalsDealsModel = compose<typeof ApprovalsModel>(
  whiteListColumn([
    'candidateName',
    'targetPopulations',
    'type',
    'area',
    'regulatoryOrganisation',
    'approvalDate',
    'associatedDocumentUrl',
  ])
)({
  ...ApprovalsModel,
  name: 'Vaccine Approvals',
  defaultSortKey: 'approvalDate',
})

export const VaccinesUnderReviewModel = compose<typeof ApprovalsModel>(
  whiteListColumn([
    'candidateName',
    'type',
    'regulatoryOrganisation',
    'sponsors',
    'affiliates',
    'associatedDocumentUrl',
  ])
)({
  ...ApprovalsModel,
  name: 'Vaccines Under Review',
  displayKey: 'candidateName',
  defaultSortKey: 'candidateName',
})

export const VaccinesPressReleasesModel = compose<typeof PressReleasesModel>(
  addOrOverrideColumn<typeof PressReleasesModel>(
    {
      key: 'vaccine',
      type: ValueType.MULTI,
      label: 'Vaccines',
      width: 200,
      graphType: 'Pie',
      customDataFilter: (data) => customFilterNaNGraphData(data, 'vaccine'),
    },
    5
  ),
  whiteListColumn<typeof PressReleasesModel>([
    'title',
    'datePublished',
    'afTopic',
    'sourceUrl',
    'vaccine',
    'description',
    'companies',
    'afTags',
  ])
)(PressReleasesModel)

export const VaccinesMediaModel = compose<typeof MediaModel>(
  addOrOverrideColumn<typeof MediaModel>(
    {
      key: 'vaccine',
      type: ValueType.MULTI,
      label: 'Vaccines',
      width: 200,
      graphType: 'Pie',
      customDataFilter: (data) => customFilterNaNGraphData(data, 'vaccine'),
    },
    5
  ),
  whiteListColumn([
    'title',
    'source',
    'afTopic',
    'datePublished',
    'link',
    'vaccine',
    'name',
    'description',
    'language',
    'companyResearchInstitution',
    'afTags',
  ])
)(MediaModel)

export const CountriesDetailViewModal = compose<typeof CountriesModel>(
  blackListColumn([
    'luCovaxH1Deliveries',
    'afWillingnessToTakeA_COVID19Vaccine',
    'afTotalVaccinations',
    'afPeopleVaccinatedOneDose',
    'afPeopleFullyVaccinated',
    'fAged20Plus',
    'dailyTestsPerThousand',
    'afNewTests',
    'afTotalTests',
    'afTotalTestsPerThousand',
    'afNewTestsSmoothed',
    'afPositiveRate',
    'timeInterval',
    'afStartDateForThirdDoses',
    'afBoostersAdministered',
    'afBoostersAdministeredPerHundred',
    'afNewTestsPerThousand',
  ])
)(CountriesModel)
